import './Dashboard.css'

import React, { useMemo, useRef } from 'react'
import { useState, useEffect } from 'react'
import { BackButton, NavBar } from '@/components/layout/NavBar'
import { Services } from '@/services/services'

import { CurrentSession } from '@/services/repository/session'
import { MainPageLayout } from '@/components/layout/MainLayout'
import { StandaloneDropdown } from '@/components/forms/common/StandaloneDropdown'
import { MachineStatisticMap } from './components/Map/Map'
import { CountHitBox } from './components/hits/CountHitBox'
import { MaxHitBox } from './components/hits/MaxHitBox'
import { useTranslations } from '@/hooks/useTranslations'
import { MinSOCBox } from './components/soc/MinSOCBox'
import { CountSOCBox } from './components/soc/CountSOCBox'
import { ActiveCounterBox } from './components/machine/ActiveCounterBox'
import { FailureCounterBox } from './components/machine/FailureCounterBox'
import { ResolveFailureCounterBox } from './components/machine/ResolveFailureCounterBox'
import { NewFailureCounterBox } from './components/machine/NewFailureCounterBox'
import { Roles } from '@/services/repository/users'
import { SearchInput } from '@/components/search/SearchInput'
import { MapResponse, MapRowMachine, mergeMapResponses } from './components/Map/Response'
import { MachineStatisticList, MachineStatisticListDefaultOrder, MachineStatisticListOrderCols } from './components/Map/List'
import { DatePeriod, datePeriodToQueryParams, DateTimePeriodBody, decodeDatePeriod, StretchOfTime, stretchOfTimeToSeconds, updateDatePeriodSearch } from '@/popups/dateTimePeriod'
import { useModal } from '@/components/modal/Modal'
import { formatDate } from '@/common/time'
import { StaticPaginatedTableState } from '@/components/search/StaticPaginatedTable'

import { useNavigate, useSearchParams } from 'react-router-dom'
// import { Order, OrderManager } from './components/Map/Order'
// import { Order, OrderManager } from './components/or'
import { Spinner } from '@/components/spinner'
import { FailureResponse, HitsData, SessionsResponse, SessionStat, UseOfStatus } from '@/pages/communal/statistics/components/common/data'
import { SearchCriteria, SearchCriteriaParams } from '@/pages/communal/statistics/components/common/searchCriteria'
import { SessionComparableFns, SessionDefaultOrder, SessionList, SessionListOrderCols } from '@/pages/communal/statistics/sessions/SessionList'
import { HistoryUser, sendHistoriesRequest, UserDeviceHistoriesData, UserDeviceHistoriesMachine } from '@/pages/communal/statistics/requests/histories'
import { updateSearchParams } from '@/common/reactnav'
import { FailuresTextBox } from '@/pages/communal/statistics/components/statistic/FailuresTextBox'
import { UseOfStatusBox } from '@/pages/communal/statistics/components/statistic/UseOfStatusBox'
import { UseOfTextBox } from '@/pages/communal/statistics/components/statistic/UseOfTextBox'
import { CommunalMachinePathMap } from './components/Map/PathMap'
import { SessionPopBody } from '../sessions/SessionPop'
import { MachineName } from '@/components/machine'
import { Order, OrderManager } from '@/components/order/Order'
import { MenuBox } from '../components/statistic/MenuBox'

type UserGroup = {
  id: string
  name: string
};

export const MachinesStatistics = ({ services, session }: { services: Services, session: CurrentSession }) => {
  const translates = useTranslations(services, session)
  const navigate = useNavigate()
  const { openModal, refreshModals } = useModal()
  const [searchParams] = useSearchParams();
  const [inMachine, setInMachine] = useState(searchParams.get("machine"))
  const [sessions, setSessions] = useState<SessionsResponse>()
  // const pollingRef = useRef<NodeJS.Timeout | null>(null)
  const [historiesData, setHistoriesData] = useState<UserDeviceHistoriesData | null | undefined>()
  const [userGroups, setUserGroups] = useState<UserGroup[]>()
  const [userGroupId, setUserGroupId] = useState<string>('')
  const [machine, setMachine] = useState<MapRowMachine>()
  const [failures, setFailures] = useState<FailureResponse>()
  const [search, setSerach] = useState<string>('')
  const [tmpSearch, setTMPSerach] = useState<string>('')
  const [data, setData] = useState<MapResponse>()
  const [selectedUser, setSelectedUser] = useState<HistoryUser | undefined>(undefined)
  const [period, setPeriod] = useState<DatePeriod>(decodeDatePeriod(searchParams))
  const [listState, setListState] = useState<StaticPaginatedTableState>({
    pageNumber: 1,
    pageSize: 5
  })
  const [listOrderManager, setListOrderManager] = useState<OrderManager>(new OrderManager(MachineStatisticListOrderCols, MachineStatisticListDefaultOrder, (order: Order) => {
    setListOrderManager(listOrderManager.newManager(order))
    setListState({
      ...listState,
      pageNumber: 1
    })
  }))
  const [sessionPagginationState, setSessionPagginationState] = useState<StaticPaginatedTableState>({
    pageNumber: 1,
    pageSize: 20
  })
  const [sessionOrderManager, setSessionOrderManager] = useState<OrderManager>(new OrderManager(SessionListOrderCols, SessionDefaultOrder, (order: Order) => {
    setSessionOrderManager(sessionOrderManager.newManager(order))
    setSessionPagginationState({
      ...sessionPagginationState,
      pageNumber: 1
    })
  }, SessionComparableFns))

  const criteria: SearchCriteria = useMemo(() => ({
    userGroupId,
    machineId: machine?.id,
    period,
    search,
  }), [userGroupId, machine, search, period])

  useEffect(() => {
    updateSearchParams(location.search, navigate, {
      ...(criteria.period ? datePeriodToQueryParams(criteria.period) : {}),
      machine: criteria.machineId
    });
  }, [criteria]);

  const statistic: UseOfStatus | undefined | null = useMemo(() => {
    if (!sessions || !period.length) {
      return null
    }
    return {
      activeTime: sessions.reduce((sum, session) => sum + (session.activeTime || 0), 0),
      onFireTime: sessions.reduce((sum, session) => sum + (session.onFireTime || 0), 0),
      totalTime: stretchOfTimeToSeconds(period.length)
    }
  }, [sessions, period])

  const hits: HitsData | undefined | null = useMemo(() => (sessions ? {
    totalHits: sessions.reduce((sum, session) => sum + (session.totalHits || 0), 0),
    maxHitX: sessions.reduce((max, session) => Math.max(max, Number(session.maxHitX)), 0),
    maxHitY: sessions.reduce((max, session) => Math.max(max, Number(session.maxHitY)), 0),
    maxHitZ: sessions.reduce((max, session) => Math.max(max, Number(session.maxHitZ)), 0),
    avgHit: (() => {
      const maxAcc = sessions.reduce((maxAcc, session) => maxAcc + Math.max(Number(session.avgHitX), Number(session.avgHitY), Number(session.avgHitZ)) * session.totalHits, 0)
      const totalHits = sessions.reduce((totalHits, session) => totalHits + session.totalHits, 0)
      return totalHits ? maxAcc / totalHits : 0
    })(),
  } : sessions), [sessions])

  const filtredSessions = useMemo<SessionsResponse | undefined>(() => {
    if (!sessions) {
      return sessions
    }
    if (!selectedUser) {
      return sessions
    }
    return sessions.filter(row => row.userId === selectedUser.id)
  }, [sessions, selectedUser])

  function goToUserPage(userId: string) {
    navigate(`/communal/statistics/users?user=${userId}&for=${criteria.period?.for}&length=${criteria.period?.length}`)
  }

  function goToMachinePage(machineId: string) {
    const rowIndex = data?.rows?.findIndex(row => machineId === row.machine.id)
    if (rowIndex) {
      setMachine(data?.rows?.[rowIndex].machine)
      setListState({
        ...listState,
        pageNumber: 1 + Math.floor(rowIndex / listState.pageSize)
      })
    }
    navigate(`/communal/statistics/machines?machine=${machineId}&for=${criteria.period?.for}&length=${criteria.period?.length}`)
  }

  // async function refreshMap(fetchUrl: string): Promise<MapResponse> {
  //   return services.session.fetchWithToken(fetchUrl).then(async (r) => {
  //     if (r.status !== 200) {
  //       const erros = await r.json()
  //       console.error(erros)
  //       return Promise.reject<MapResponse>()
  //     }
  //     const newResponse: MapResponse = await r.json()
  //     return newResponse
  //   })
  // }

  useEffect(() => {
    if (criteria.machineId === undefined) {
      setHistoriesData(null)
      return
    }
    setHistoriesData(undefined)
    sendHistoriesRequest(services, criteria).then(setHistoriesData)
  }, [criteria])

  async function refreshMap(fetchUrl: string, oldResponse?: MapResponse): Promise<MapResponse> {
    return services.session.fetchWithToken(fetchUrl).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        console.error(erros)
        return Promise.reject<MapResponse>()
      }
      const newResponse: MapResponse = await r.json()
      newResponse.rows = newResponse.rows || null
      if (oldResponse === undefined) {
        return newResponse
      }
      return mergeMapResponses(oldResponse, newResponse)
    })
  }

  useEffect(() => {
    const fetchUrl = `/api/communal/statistics/machines/map?usergroup=${userGroupId}&search=${search}`;
    refreshMap(fetchUrl).then((data: MapResponse) => {
      let nextPageNumber = 1;
      let machineToSet = null;

      if (inMachine) {
        const ordered = listOrderManager.orderListByKey(data.rows || [])
        const index = ordered?.findIndex(
          row => row.machine.id === inMachine || row.machine.name === inMachine || row.machine.internalNumber === inMachine
        );
        if (index !== undefined && index !== -1) {
          nextPageNumber = 1 + Math.floor(index / listState.pageSize);
          machineToSet = ordered[index]?.machine ?? null;
        }
        setInMachine(null)
      }

      setListState({
        ...listState,
        pageNumber: nextPageNumber
      });

      if (machineToSet) {
        setMachine(machineToSet);
      }

      setData(data);
    });
  }, [userGroupId, search]);


  useEffect(() => {
    if (!criteria.machineId) {
      return
    }
    setSessions(undefined)
    var fetchUrl = `/api/communal/statistics/sessions/list?${SearchCriteriaParams(criteria)}`
    services.session.fetchWithToken(fetchUrl).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        console.error(erros)
        return Promise.reject<any>()
      }
      setSessions(await r.json())
      setSessionPagginationState({
        ...sessionPagginationState,
        pageNumber: 1
      })
    })
  }, [criteria]);

  useEffect(() => {
    if (data === undefined) {
      return
    }
    let handle: NodeJS.Timeout
    let isCancelled = false

    const clock = async () => {
      if (isCancelled) return;
      if (data === undefined) {
        return;
      }
      let fromDate = data?.time ? new Date(data?.time).toISOString() : ''
      var fetchUrl = `/api/communal/statistics/machines/map?usergroup=${userGroupId}&search=${search}&fromDate=${fromDate}`
      await refreshMap(fetchUrl, data).then(setData);
      if (!isCancelled) {
        handle = setTimeout(clock, 2000)
      }
    }

    handle = setTimeout(clock, 2000);

    return () => {
      isCancelled = true;
      clearTimeout(handle);
    };
  }, [data, userGroupId, search]);

  useEffect(() => {
    setUserGroups(undefined)
    services.session.fetchWithToken(`/api/communal/usergroups/my`).then((r) => r.json()).then((data) => {
      if (session.user.role === Roles.SuperAdmin) {
        setUserGroupId('')
      } else {
        setUserGroupId(data[0].id)
      }
      setUserGroups(data)
    })
  }, [])

  useEffect(() => {
    if (!criteria.machineId) {
      return
    }
    setFailures(undefined)
    var fetchUrl = `/api/communal/statistics/failures?${SearchCriteriaParams(criteria)}`
    services.session.fetchWithToken(fetchUrl).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        console.error(erros)
        return Promise.reject<any>()
      }
      setFailures(await r.json())
    })
  }, [criteria]);

  const machineTitle = () => {
    if (!machine) {
      return null
    }
    return machine ? <h3 className='title-link' onClick={() => navigate(`/communal/machine/${machine.id}`)}>{[
      machine.type.manufacturer,
      machine.type.category ? session.translates.for(`machine.types.${machine.type.category}`) : null,
      machine.type.model,
      machine.internalNumber,
    ].filter(val => val != null && val !== '').join(' > ')} <i className="svgfont-link" /> </h3> : null
  }

  const openDetails = (row: SessionStat) => {
    if (!historiesData) {
      return
    }
    openModal((props) => SessionPopBody({ ...props, session, services, row, user: undefined, machine, historiesData }))
  }

  const openMaxHit = () => {
    const maxHitSession = sessions?.reduce((maxSession, session) =>
      Math.max(session.maxHitX, session.maxHitY, session.maxHitZ) > Math.max(maxSession.maxHitX, maxSession.maxHitY, maxSession.maxHitZ) ? session : maxSession
    );
    maxHitSession && openDetails(maxHitSession)
  }

  const openAvgList = () => {
    if (!historiesData || !sessions || !machine) {
      return
    }
    openModal((props) => {
      const filtredSessions = sessions?.filter(s => s.maxHitX || s.maxHitY || s.maxHitZ)
      const [sessionPagginationState, setSessionPagginationState] = useState<StaticPaginatedTableState>({
        pageNumber: 1,
        pageSize: 8
      })
      const [sessionOrderManager, setSessionOrderManager] = useState<OrderManager>(new OrderManager(SessionListOrderCols, {
        key: 'maxHit',
        asc: false
      }, (order: Order) => {
        setSessionOrderManager(sessionOrderManager.newManager(order))
        setSessionPagginationState({
          ...sessionPagginationState,
          pageNumber: 1
        })
      }, SessionComparableFns))

      useEffect(refreshModals, [sessionPagginationState, sessionOrderManager])

      return <div className='sessions-hits-popup'>
        <h2><MachineName row={machine} /></h2>
        <div className='user-map dashboard-grid-section'>
          <SessionList pagginationState={sessionPagginationState} setPagginationState={setSessionPagginationState} services={services} session={session} machine={machine} data={filtredSessions} historiesData={historiesData} goToUserPage={goToUserPage} goToMachinePage={goToMachinePage} orderManager={sessionOrderManager} />
        </div>
      </div>
    })
  }

  const goToMachineFailures = () => {
    window.open(`/communal/machine/${machine?.id}/failures`, '_blank')
  }

  if (translates === undefined || userGroups === undefined || !!inMachine) {
    return <Spinner />
  }

  return <MainPageLayout pageName='communal-machinestatistic' services={services} translates={translates} session={session}
    navbarLeft={<BackButton services={services} translates={session.translates} />}>

    <div className='dashboard-navigation'>
      <div className='top-icon-button' onClick={() => openModal((props) => DateTimePeriodBody({
        ...props,
        services,
        session,
        translates: session.translates,
        period,
        setter: setPeriod
      }))}>
        <i className='svgfont-calendar' />
        <span>
          {session.translates.for('page.dashboard.global.for_datetime').replace('{{for_date}}', period.for ? formatDate(period.for) : translates.for('popup.datetime_period.now')).replace('{{for_length}}', translates.for(`dicts.period_length.${period.length}`))}
        </span>
      </div>

      <form className='main-form-slim main-form-two-col'>
        <div className='form-field'>
          <div>
            <StandaloneDropdown
              value={userGroupId}
              onChange={setUserGroupId}
              options={[
                ...(session.user.role === Roles.SuperAdmin ? [{
                  value: '',
                  label: session.translates.for('page.dashboard.global.all_groups')
                }] : []),
                ...(userGroups || []).map(row => ({
                  value: row.id,
                  label: row.name,
                }))
              ]}
              readOnly={!!machine}
            />
          </div>
        </div>
        <div className='form-field'>
          <SearchInput
            className='search-input-block'
            value={tmpSearch}
            onChange={setTMPSerach}
            onDebouncedChange={setSerach}
            delay={500}
            translates={translates}
            readOnly={!!machine}
          />
        </div>
      </form>
    </div>

    <div className='top-boxes'>
      <div className='dashboard-global-box-container'>
        <div className='machine-list dashboard-grid-section'>
          <MachineStatisticList
            parentState={listState}
            setParentState={setListState}
            services={services} session={session} data={data}
            selected={machine}
            onSelect={(selected: MapRowMachine | undefined) => {
              setMachine(selected)
            }}
            orderManager={listOrderManager}
          />
        </div>
        <div className='machine-map dashboard-grid-section'>
          <MachineStatisticMap services={services} session={session} data={machine?.id !== undefined ? {
            time: data?.time || '',
            rows: data?.rows?.filter((row) => row.machine.id === machine?.id) || []
          } : data} />
        </div>
      </div>
      {/* <DashboardMapComponent services={services} session={session} fetchUrl={`/api/communal/statistics/machines/map?usergroup=${userGroupId}&search=${search}`} /> */}
    </div>

    {machineTitle()}

    <div className="four-column-container">
      <div className="four-col dashboard-grid-section">
        <CountHitBox services={services} session={session} criteria={criteria} />
      </div>
      <div className="four-col dashboard-grid-section">
        <MaxHitBox services={services} session={session} criteria={criteria} goToMachinePage={goToMachinePage} goToUserPage={goToUserPage} />
      </div>
      <div className="four-col dashboard-grid-section">
        <CountSOCBox services={services} session={session} criteria={criteria} goToMachinePage={goToMachinePage} goToUserPage={goToUserPage} />
      </div>
      <div className="four-col dashboard-grid-section">
        <MinSOCBox services={services} session={session} criteria={criteria} goToMachinePage={goToMachinePage} goToUserPage={goToUserPage} />
      </div>
    </div>

    <div className="four-column-container">
      <div className="four-col dashboard-grid-section">
        <ActiveCounterBox services={services} session={session} criteria={criteria} />
      </div>
      <div className="four-col dashboard-grid-section">
        <NewFailureCounterBox services={services} session={session} criteria={criteria} />
      </div>
      <div className="four-col dashboard-grid-section">
        <ResolveFailureCounterBox services={services} session={session} criteria={criteria} />
      </div>
      <div className="four-col dashboard-grid-section">
        <FailureCounterBox services={services} session={session} criteria={criteria} />
      </div>
    </div>

    {machine ? <div className='top-boxes'>
      {machineTitle()}
      {/* <div className="four-col dashboard-grid-section">
        {statistic ? <ActivityStatusBox services={services} session={session} activityStatus={statistic} /> : statistic === null ? translates.for('app.no_data') : <Spinner />}
      </div>
      <div className="four-col dashboard-grid-section">
        {statistic ? <ActivityTextBox services={services} session={session} activityStatus={statistic} /> : statistic === null ? translates.for('app.no_data') : <Spinner />}
      </div> */}
      <div className="four-column-container">
        <div className="four-col dashboard-grid-section">
          {statistic ? <UseOfStatusBox services={services} session={session} status={statistic} /> : statistic === null ? translates.for('app.no_data') : <Spinner />}
        </div>
        <div className="four-col dashboard-grid-section">
          {statistic ? <UseOfTextBox services={services} session={session} status={statistic} /> : statistic === null ? translates.for('app.no_data') : <Spinner />}
        </div>
        <div className="four-col dashboard-grid-section">
          {failures ? <FailuresTextBox services={services} session={session} failures={failures} hits={hits}
            onMaxHitsClick={openMaxHit}
            onAvgHitsClick={openAvgList}
            onServicemanCounterClick={goToMachineFailures}
            onUserCounterClick={goToMachineFailures}
          /> : <Spinner />}
        </div>
        <div className="four-col dashboard-grid-section">
          { machine?.id ? <MenuBox forDay={period.for || new Date()} machine={machine} /> : null }
        </div>
      </div>

      {session && historiesData ? <div className='top-boxes'>
        <div className='dashboard-box-container'>
          <div className='user-map dashboard-grid-section'>
            <CommunalMachinePathMap services={services} session={session} historiesData={historiesData} selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
          </div>
        </div>
      </div> : historiesData === undefined ? <Spinner /> : null}

      {filtredSessions && historiesData ? <div className='dashboard-box-container'>
        <div className='user-map dashboard-grid-section'>
          <SessionList pagginationState={sessionPagginationState} setPagginationState={setSessionPagginationState} services={services} session={session} machine={machine} data={filtredSessions} historiesData={historiesData} goToUserPage={goToUserPage} goToMachinePage={goToMachinePage} orderManager={sessionOrderManager} />
        </div>
      </div> : filtredSessions === null || historiesData === null ? null : <Spinner />}

    </div> : null
    }

  </MainPageLayout >
}
